import { AnimatePresence, motion } from 'framer-motion';
import React, { SetStateAction, useEffect, useRef, useState } from 'react';
import MuteIcon from '../../../assets/Controls/MuteIcon';
import PauseIcon from '../../../assets/Controls/PauseIcon';
import PlayIcon from '../../../assets/Controls/PlayIcon';
import VolumeIcon from '../../../assets/Controls/VolumeIcon';
import LikeIcon from '../../../assets/Feed/LikeIcon';
import ShareIcon from '../../../assets/Posts/ShareIcon';
import { TimeLineDataType } from '../../../services/apiTypes';
import User from '../../User';
import {
  ControlButtonWrapper,
  Controls,
  ProgressBarContainer,
  ReplyForm,
  ReplyInput,
  StoriesContainer,
  StoryFooterContainer,
  StoryHeaderContainer,
  StoryImage,
  UserContainer
} from './styles';

type StoriesProps = {
  isCurrent: boolean;
  stories: TimeLineDataType;
  setCurrentCardIndex: React.Dispatch<SetStateAction<number>>;
};

export default function InstaStories({ stories, isCurrent, setCurrentCardIndex }: StoriesProps) {
  const [currentImage, setCurrentImage] = useState(stories.medias[0].url);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const intervalRef = useRef<any>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);

  const handlePlayPause = () => {
    setIsPlaying(prev => !prev);
  };

  const handleMute = () => {
    setIsMuted(prev => !prev);
  };

  useEffect(() => {
    if (isCurrent && isPlaying) {
      intervalRef.current = setInterval(() => {
        if (currentStoryIndex < stories.medias.length - 1) {
          setCurrentStoryIndex(currentStoryIndex + 1);
          setCurrentImage(stories.medias[currentStoryIndex + 1].url);
        } else {
          setCurrentCardIndex(prev => prev + 1);
        }
      }, 5000);
      return () => clearInterval(intervalRef.current);
    }
  }, [currentStoryIndex, isCurrent, stories, isPlaying]);

  const [liked, setLiked] = useState(false);
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <StoriesContainer>
          <StoryHeaderContainer>
            <ProgressBarContainer>
              {stories && stories.medias.map((_: any, index: number) =>
                <ProgressBarComponent
                  key={index}
                  count={stories.medias.length}
                  index={index}
                  currentStoryIndex={currentStoryIndex}
                  defaultDuration={5000}
                  isCurrent={isCurrent}
                  isPlaying={isPlaying}
                />
              )}
            </ProgressBarContainer>
            <UserContainer>
              <User light={true} horizontal={true} icon={stories.authorAvatarUrl} username={stories.authorUsername} timeSince={stories.createdAt} />
              <Controls>
                <ControlButtonWrapper onClick={() => handlePlayPause()} >{isPlaying ? <PauseIcon /> : <PlayIcon />}</ControlButtonWrapper>
                <ControlButtonWrapper onClick={() => handleMute()} >{isMuted ? <MuteIcon /> : <VolumeIcon />}</ControlButtonWrapper>
              </Controls>
            </UserContainer>
          </StoryHeaderContainer>
          <StoryImage src={currentImage} />
          <StoryFooterContainer>
            <ReplyForm>
              <LeftInput rotation={0} />
              <ReplyInput placeholder={`Responder a ${stories.authorUsername}...`} />
              <LeftInput rotation={180} />
            </ReplyForm>
            <LikeIcon liked={liked} />
            <ShareIcon />
          </StoryFooterContainer>
        </StoriesContainer>
      </motion.div>
    </AnimatePresence>
  );
}

type ProgressBarProps = {
  index: number;
  count: number;
  currentStoryIndex: number;
  defaultDuration: number;
  isCurrent: boolean;
  isPlaying: boolean;
};

const ProgressBarComponent = ({ index, count, currentStoryIndex, defaultDuration, isCurrent, isPlaying }: ProgressBarProps) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setProgress(index < currentStoryIndex ? 100 : 0);
    if (!isPlaying) {
      setProgress(progress)
      return;
    }
    if (index === currentStoryIndex && isCurrent) {
      const interval = setInterval(() => {
        setProgress((prevProgress) => {
          const nextProgress = prevProgress + (1000 / defaultDuration);
          if (nextProgress >= 100) {
            clearInterval(interval);
            return 100;
          }
          return nextProgress;
        });
      }, 10);

      return () => clearInterval(interval);
    }
  }, [index, currentStoryIndex, defaultDuration, isCurrent, isPlaying]);

  return (
    <div
      style={{
        width: `${100 / count}%`,
        height: '3px',
        borderRadius: '20px',
        backgroundColor: '#999999'
      }}
    >
      <div
        style={{
          backgroundColor: '#D9D9D9',
          height: '3px',
          borderRadius: '20px',
          width: `${progress}%`
        }}
      />
    </div>
  );
}

function LeftInput({ rotation }: { rotation: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="40"
      shapeRendering="crispEdges"
      style={{ transform: `rotate(${rotation}deg)` }}
    >
      <path fill="#FFF" d="M8 0H9V1H8z"></path>
      <path fill="#FFF" d="M9 0H10V1H9z"></path>
      <path fill="#FFF" d="M10 0H11V1H10z"></path>
      <path fill="#FFF" d="M11 0H12V1H11z"></path>
      <path fill="#FFF" d="M8 1H9V2H8z"></path>
      <path fill="#FFF" d="M9 1H10V2H9z"></path>
      <path fill="#FFF" d="M10 1H11V2H10z"></path>
      <path fill="#FFF" d="M11 1H12V2H11z"></path>
      <path fill="#FFF" d="M8 2H9V3H8z"></path>
      <path fill="#FFF" d="M9 2H10V3H9z"></path>
      <path fill="#FFF" d="M10 2H11V3H10z"></path>
      <path fill="#FFF" d="M11 2H12V3H11z"></path>
      <path fill="#FFF" d="M8 3H9V4H8z"></path>
      <path fill="#FFF" d="M9 3H10V4H9z"></path>
      <path fill="#FFF" d="M10 3H11V4H10z"></path>
      <path fill="#FFF" d="M11 3H12V4H11z"></path>
      <path fill="#FFF" d="M4 4H5V5H4z"></path>
      <path fill="#FFF" d="M5 4H6V5H5z"></path>
      <path fill="#FFF" d="M6 4H7V5H6z"></path>
      <path fill="#FFF" d="M7 4H8V5H7z"></path>
      <path fill="#FFF" d="M4 5H5V6H4z"></path>
      <path fill="#FFF" d="M5 5H6V6H5z"></path>
      <path fill="#FFF" d="M6 5H7V6H6z"></path>
      <path fill="#FFF" d="M7 5H8V6H7z"></path>
      <path fill="#FFF" d="M4 6H5V7H4z"></path>
      <path fill="#FFF" d="M5 6H6V7H5z"></path>
      <path fill="#FFF" d="M6 6H7V7H6z"></path>
      <path fill="#FFF" d="M7 6H8V7H7z"></path>
      <path fill="#FFF" d="M4 7H5V8H4z"></path>
      <path fill="#FFF" d="M5 7H6V8H5z"></path>
      <path fill="#FFF" d="M6 7H7V8H6z"></path>
      <path fill="#FFF" d="M7 7H8V8H7z"></path>
      <path fill="#FFF" d="M4 8H5V9H4z"></path>
      <path fill="#FFF" d="M5 8H6V9H5z"></path>
      <path fill="#FFF" d="M6 8H7V9H6z"></path>
      <path fill="#FFF" d="M7 8H8V9H7z"></path>
      <path fill="#FFF" d="M0 9H1V10H0z"></path>
      <path fill="#FFF" d="M1 9H2V10H1z"></path>
      <path fill="#FFF" d="M2 9H3V10H2z"></path>
      <path fill="#FFF" d="M3 9H4V10H3z"></path>
      <path fill="#FFF" d="M0 10H1V11H0z"></path>
      <path fill="#FFF" d="M1 10H2V11H1z"></path>
      <path fill="#FFF" d="M2 10H3V11H2z"></path>
      <path fill="#FFF" d="M3 10H4V11H3z"></path>
      <path fill="#FFF" d="M0 11H1V12H0z"></path>
      <path fill="#FFF" d="M1 11H2V12H1z"></path>
      <path fill="#FFF" d="M2 11H3V12H2z"></path>
      <path fill="#FFF" d="M3 11H4V12H3z"></path>
      <path fill="#FFF" d="M0 12H1V13H0z"></path>
      <path fill="#FFF" d="M1 12H2V13H1z"></path>
      <path fill="#FFF" d="M2 12H3V13H2z"></path>
      <path fill="#FFF" d="M3 12H4V13H3z"></path>
      <path fill="#FFF" d="M0 13H1V14H0z"></path>
      <path fill="#FFF" d="M1 13H2V14H1z"></path>
      <path fill="#FFF" d="M2 13H3V14H2z"></path>
      <path fill="#FFF" d="M3 13H4V14H3z"></path>
      <path fill="#FFF" d="M0 14H1V15H0z"></path>
      <path fill="#FFF" d="M1 14H2V15H1z"></path>
      <path fill="#FFF" d="M2 14H3V15H2z"></path>
      <path fill="#FFF" d="M3 14H4V15H3z"></path>
      <path fill="#FFF" d="M0 15H1V16H0z"></path>
      <path fill="#FFF" d="M1 15H2V16H1z"></path>
      <path fill="#FFF" d="M2 15H3V16H2z"></path>
      <path fill="#FFF" d="M3 15H4V16H3z"></path>
      <path fill="#FFF" d="M0 16H1V17H0z"></path>
      <path fill="#FFF" d="M1 16H2V17H1z"></path>
      <path fill="#FFF" d="M2 16H3V17H2z"></path>
      <path fill="#FFF" d="M3 16H4V17H3z"></path>
      <path fill="#FFF" d="M0 17H1V18H0z"></path>
      <path fill="#FFF" d="M1 17H2V18H1z"></path>
      <path fill="#FFF" d="M2 17H3V18H2z"></path>
      <path fill="#FFF" d="M3 17H4V18H3z"></path>
      <path fill="#FFF" d="M0 18H1V19H0z"></path>
      <path fill="#FFF" d="M1 18H2V19H1z"></path>
      <path fill="#FFF" d="M2 18H3V19H2z"></path>
      <path fill="#FFF" d="M3 18H4V19H3z"></path>
      <path fill="#FFF" d="M0 19H1V20H0z"></path>
      <path fill="#FFF" d="M1 19H2V20H1z"></path>
      <path fill="#FFF" d="M2 19H3V20H2z"></path>
      <path fill="#FFF" d="M3 19H4V20H3z"></path>
      <path fill="#FFF" d="M0 20H1V21H0z"></path>
      <path fill="#FFF" d="M1 20H2V21H1z"></path>
      <path fill="#FFF" d="M2 20H3V21H2z"></path>
      <path fill="#FFF" d="M3 20H4V21H3z"></path>
      <path fill="#FFF" d="M0 21H1V22H0z"></path>
      <path fill="#FFF" d="M1 21H2V22H1z"></path>
      <path fill="#FFF" d="M2 21H3V22H2z"></path>
      <path fill="#FFF" d="M3 21H4V22H3z"></path>
      <path fill="#FFF" d="M0 22H1V23H0z"></path>
      <path fill="#FFF" d="M1 22H2V23H1z"></path>
      <path fill="#FFF" d="M2 22H3V23H2z"></path>
      <path fill="#FFF" d="M3 22H4V23H3z"></path>
      <path fill="#FFF" d="M0 23H1V24H0z"></path>
      <path fill="#FFF" d="M1 23H2V24H1z"></path>
      <path fill="#FFF" d="M2 23H3V24H2z"></path>
      <path fill="#FFF" d="M3 23H4V24H3z"></path>
      <path fill="#FFF" d="M0 24H1V25H0z"></path>
      <path fill="#FFF" d="M1 24H2V25H1z"></path>
      <path fill="#FFF" d="M2 24H3V25H2z"></path>
      <path fill="#FFF" d="M3 24H4V25H3z"></path>
      <path fill="#FFF" d="M0 25H1V26H0z"></path>
      <path fill="#FFF" d="M1 25H2V26H1z"></path>
      <path fill="#FFF" d="M2 25H3V26H2z"></path>
      <path fill="#FFF" d="M3 25H4V26H3z"></path>
      <path fill="#FFF" d="M0 26H1V27H0z"></path>
      <path fill="#FFF" d="M1 26H2V27H1z"></path>
      <path fill="#FFF" d="M2 26H3V27H2z"></path>
      <path fill="#FFF" d="M3 26H4V27H3z"></path>
      <path fill="#FFF" d="M0 27H1V28H0z"></path>
      <path fill="#FFF" d="M1 27H2V28H1z"></path>
      <path fill="#FFF" d="M2 27H3V28H2z"></path>
      <path fill="#FFF" d="M3 27H4V28H3z"></path>
      <path fill="#FFF" d="M0 28H1V29H0z"></path>
      <path fill="#FFF" d="M1 28H2V29H1z"></path>
      <path fill="#FFF" d="M2 28H3V29H2z"></path>
      <path fill="#FFF" d="M3 28H4V29H3z"></path>
      <path fill="#FFF" d="M0 29H1V30H0z"></path>
      <path fill="#FFF" d="M1 29H2V30H1z"></path>
      <path fill="#FFF" d="M2 29H3V30H2z"></path>
      <path fill="#FFF" d="M3 29H4V30H3z"></path>
      <path fill="#FFF" d="M0 30H1V31H0z"></path>
      <path fill="#FFF" d="M1 30H2V31H1z"></path>
      <path fill="#FFF" d="M2 30H3V31H2z"></path>
      <path fill="#FFF" d="M3 30H4V31H3z"></path>
      <path fill="#FFF" d="M4 31H5V32H4z"></path>
      <path fill="#FFF" d="M5 31H6V32H5z"></path>
      <path fill="#FFF" d="M6 31H7V32H6z"></path>
      <path fill="#FFF" d="M7 31H8V32H7z"></path>
      <path fill="#FFF" d="M4 32H5V33H4z"></path>
      <path fill="#FFF" d="M5 32H6V33H5z"></path>
      <path fill="#FFF" d="M6 32H7V33H6z"></path>
      <path fill="#FFF" d="M7 32H8V33H7z"></path>
      <path fill="#FFF" d="M4 33H5V34H4z"></path>
      <path fill="#FFF" d="M5 33H6V34H5z"></path>
      <path fill="#FFF" d="M6 33H7V34H6z"></path>
      <path fill="#FFF" d="M7 33H8V34H7z"></path>
      <path fill="#FFF" d="M4 34H5V35H4z"></path>
      <path fill="#FFF" d="M5 34H6V35H5z"></path>
      <path fill="#FFF" d="M6 34H7V35H6z"></path>
      <path fill="#FFF" d="M7 34H8V35H7z"></path>
      <path fill="#FFF" d="M4 35H5V36H4z"></path>
      <path fill="#FFF" d="M5 35H6V36H5z"></path>
      <path fill="#FFF" d="M6 35H7V36H6z"></path>
      <path fill="#FFF" d="M7 35H8V36H7z"></path>
      <path fill="#FFF" d="M8 36H9V37H8z"></path>
      <path fill="#FFF" d="M9 36H10V37H9z"></path>
      <path fill="#FFF" d="M10 36H11V37H10z"></path>
      <path fill="#FFF" d="M11 36H12V37H11z"></path>
      <path fill="#FFF" d="M8 37H9V38H8z"></path>
      <path fill="#FFF" d="M9 37H10V38H9z"></path>
      <path fill="#FFF" d="M10 37H11V38H10z"></path>
      <path fill="#FFF" d="M11 37H12V38H11z"></path>
      <path fill="#FFF" d="M8 38H9V39H8z"></path>
      <path fill="#FFF" d="M9 38H10V39H9z"></path>
      <path fill="#FFF" d="M10 38H11V39H10z"></path>
      <path fill="#FFF" d="M11 38H12V39H11z"></path>
      <path fill="#FFF" d="M8 39H9V40H8z"></path>
      <path fill="#FFF" d="M9 39H10V40H9z"></path>
      <path fill="#FFF" d="M10 39H11V40H10z"></path>
      <path fill="#FFF" d="M11 39H12V40H11z"></path>
    </svg>
  );
}